import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play, Square, Loader2, Map as MapIcon, Route, Timer, Footprints, CalendarClock, ChevronRight, ArrowLeft, Satellite, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage, fmtTime, fmtShortDate, toISODate } from '../../lib/easerApi';
import { RouteMap, formatDistance, formatDuration } from './RouteMap';

const FLUSH_MS = 8000;
const LOCAL_KEY = 'easer_tract_session';

/** Live GPS tracking of a flyer distribution: start, follow on map, stop with a summary. */
export function EaserTracts({ onUnauthorized }) {
  const [params, setParams] = useSearchParams();
  const [session, setSession] = useState(undefined); // undefined = loading, null = none
  const [summary, setSummary] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [history, setHistory] = useState(null);
  const [planned, setPlanned] = useState([]);
  const [zone, setZone] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const today = toISODate(new Date());
      const [a, h, p] = await Promise.all([
        easerGet('/api/easer/tracts/active'),
        easerGet('/api/easer/tracts'),
        easerGet('/api/easer/planning', { start: today, end: today }),
      ]);
      setSession(a.data.session);
      setHistory(h.data.items);
      setPlanned((p.data.items || []).filter((e) => e.is_tract && e.status !== 'done'));
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, 'Impossible de charger les distributions'));
      setSession(null); setHistory([]);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const start = async (eventId = null, label = '') => {
    if (!navigator.geolocation) return toast.error("La géolocalisation n'est pas disponible sur cet appareil");
    setBusy(true);
    try {
      const res = await easerPost('/api/easer/tracts/start', { event_id: eventId, label: label || zone });
      setSession(res.data.session);
      localStorage.setItem(LOCAL_KEY, res.data.session.id);
      toast.success(res.data.resumed ? 'Distribution en cours reprise' : 'Distribution démarrée — bonne tournée !');
      if (params.get('event')) { params.delete('event'); setParams(params, { replace: true }); }
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Deep link from the planning tab: ?tab=tracts&event=ID (attempted once)
  const autoEvent = params.get('event');
  const startRef = useRef(start);
  startRef.current = start;
  const autoTried = useRef(null);
  useEffect(() => {
    if (session === null && autoEvent && autoTried.current !== autoEvent) {
      autoTried.current = autoEvent;
      const ev = planned.find((e) => String(e.id) === autoEvent);
      startRef.current(Number(autoEvent), ev?.label || '');
    }
  }, [session, autoEvent, planned]);

  if (session === undefined || history === null) return <div className="space-y-3"><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-24 rounded-xl" /></div>;

  if (summary) return <TractSummary session={summary} onClose={() => { setSummary(null); load(); }} />;
  if (viewing) return <TractDetail sessionId={viewing} onBack={() => setViewing(null)} onUnauthorized={onUnauthorized} />;
  if (session) return <TractLive session={session} onStopped={(s) => { localStorage.removeItem(LOCAL_KEY); setSession(null); setSummary(s); }} onUnauthorized={onUnauthorized} />;

  return (
    <div className="space-y-4" data-testid="easer-tracts">
      <Card className="p-5 border-border shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Footprints className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
          <div>
            <h2 className="font-['Space_Grotesk'] font-semibold">Distribution de tracts</h2>
            <p className="text-xs text-muted-foreground">Votre trajet est enregistré en direct par GPS et visible par l'agence.</p>
          </div>
        </div>
        {planned.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="tract-planned-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Planifiées aujourd'hui</p>
            {planned.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--brand-mint))]/20 p-3" data-testid="tract-planned-item">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{e.label}</p>
                  <p className="text-xs text-muted-foreground">{fmtTime(e.start)} – {fmtTime(e.end)}{e.location ? ` · ${e.location}` : ''}</p>
                </div>
                <Button size="sm" onClick={() => start(e.id, e.label)} disabled={busy} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="tract-start-planned"><Play className="h-4 w-4 mr-1" /> Démarrer</Button>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1.5 mb-3">
          <Label htmlFor="tract-zone">Secteur / quartier (optionnel)</Label>
          <Input id="tract-zone" value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Ex. Servins centre" className="h-11" data-testid="tract-zone-input" />
        </div>
        <Button onClick={() => start(null, zone)} disabled={busy} size="lg" className="w-full h-14 text-base bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="tract-start">
          {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />} Démarrer la distribution
        </Button>
        <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1"><Satellite className="h-3 w-3 mt-0.5 flex-shrink-0" /> Autorisez la position et gardez l'application ouverte pendant la tournée pour un tracé complet.</p>
      </Card>

      <Card className="p-5 border-border shadow-sm">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-3"><Route className="h-4 w-4" /> Mes distributions</h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="tract-history-empty">Aucune distribution enregistrée pour le moment.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="tract-history">
            {history.map((s) => (
              <li key={s.id}>
                <button type="button" onClick={() => setViewing(s.id)} className="w-full flex items-center gap-3 py-3 text-left hover:bg-[hsl(var(--secondary))]/50 rounded-md px-1" data-testid="tract-history-item">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{s.label}</p>
                    <p className="text-xs text-muted-foreground">{fmtShortDate(s.started_at)} · {fmtTime(s.started_at)} → {fmtTime(s.ended_at)}</p>
                  </div>
                  <span className="text-sm font-semibold text-[hsl(var(--primary))] tabular-nums">{formatDistance(s.distance_m)}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function useWakeLock(active) {
  useEffect(() => {
    if (!active || !navigator.wakeLock) return undefined;
    let lock = null;
    const request = async () => { try { lock = await navigator.wakeLock.request('screen'); } catch { lock = null; } };
    const onVisible = () => { if (document.visibilityState === 'visible') request(); };
    request();
    document.addEventListener('visibilitychange', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); if (lock) lock.release().catch(() => {}); };
  }, [active]);
}

function TractLive({ session, onStopped, onUnauthorized }) {
  const [trail, setTrail] = useState(session.points || []);
  const [stats, setStats] = useState({ distance_m: session.distance_m, point_count: session.point_count });
  const [elapsed, setElapsed] = useState(0);
  const [gpsError, setGpsError] = useState('');
  const [accuracy, setAccuracy] = useState(null);
  const [stopOpen, setStopOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [flyers, setFlyers] = useState('');
  const [comment, setComment] = useState('');
  const buffer = useRef([]);
  const flushing = useRef(false);
  useWakeLock(true);

  const flush = useCallback(async () => {
    if (flushing.current || buffer.current.length === 0) return;
    flushing.current = true;
    const batch = buffer.current.splice(0, 200);
    try {
      const res = await easerPost(`/api/easer/tracts/${session.id}/points`, { points: batch });
      setStats({ distance_m: res.data.distance_m, point_count: res.data.point_count });
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      if (err?.response?.status === 409) return; // session closed elsewhere
      buffer.current.unshift(...batch); // retry later (offline)
      if (buffer.current.length > 2000) buffer.current.length = 2000;
    } finally {
      flushing.current = false;
    }
  }, [session.id, onUnauthorized]);

  useEffect(() => {
    const startedAt = new Date(session.started_at).getTime();
    const tick = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    const flusher = setInterval(flush, FLUSH_MS);
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy: acc } = pos.coords;
        setGpsError('');
        setAccuracy(Math.round(acc));
        const point = { lat, lng, acc, ts: pos.timestamp || Date.now() };
        buffer.current.push(point);
        if (acc <= 80) setTrail((t) => (t.length > 5000 ? [...t.slice(-4000), point] : [...t, point]));
      },
      (err) => setGpsError(err.code === 1 ? 'Position refusée : autorisez la géolocalisation dans les réglages du navigateur.' : 'Signal GPS faible, recherche en cours…'),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
    return () => { clearInterval(tick); clearInterval(flusher); navigator.geolocation.clearWatch(watchId); };
  }, [session.id, session.started_at, flush]);

  const stop = async () => {
    setStopping(true);
    try {
      await flush();
      const res = await easerPost(`/api/easer/tracts/${session.id}/stop`, { comment, flyers: flyers === '' ? null : Number(flyers) });
      toast.success('Distribution clôturée et enregistrée');
      onStopped(res.data.session);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
    } finally {
      setStopping(false);
    }
  };

  const routes = [{ id: session.id, points: trail, color: session.color, label: session.label, live: true }];

  return (
    <div className="space-y-4" data-testid="tract-live">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] gap-1 mb-1"><span className="h-2 w-2 rounded-full bg-white animate-pulse" /> En direct</Badge>
          <p className="font-semibold truncate" data-testid="tract-live-label">{session.label}</p>
        </div>
        <p className="font-['Space_Grotesk'] text-3xl font-bold tabular-nums text-[hsl(var(--primary))]" data-testid="tract-elapsed">{formatDuration(elapsed)}</p>
      </div>
      <RouteMap routes={routes} follow height={300} />
      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Route} label="Distance" value={formatDistance(stats.distance_m)} testId="tract-distance" />
        <Stat icon={MapIcon} label="Points GPS" value={stats.point_count} testId="tract-points" />
        <Stat icon={Satellite} label="Précision" value={accuracy ? `±${accuracy} m` : '…'} />
      </div>
      {gpsError && <p className="text-sm text-destructive flex items-start gap-1.5" data-testid="tract-gps-error"><AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {gpsError}</p>}
      <Button onClick={() => setStopOpen(true)} size="lg" className="w-full h-14 text-base bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="tract-stop">
        <Square className="h-5 w-5 mr-2" /> Clôturer la distribution
      </Button>

      <Dialog open={stopOpen} onOpenChange={(o) => { if (!stopping) setStopOpen(o); }}>
        <DialogContent className="max-w-md w-[95vw] rounded-2xl" data-testid="tract-stop-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk']">Clôturer la distribution</DialogTitle>
            <DialogDescription>{formatDistance(stats.distance_m)} parcourus en {formatDuration(elapsed)}. Le trajet sera enregistré et visible par l'agence.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tract-flyers">Nombre de tracts distribués (optionnel)</Label>
              <Input id="tract-flyers" type="number" inputMode="numeric" min="0" value={flyers} onChange={(e) => setFlyers(e.target.value)} className="h-11" data-testid="tract-flyers" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tract-comment">Remarque (optionnel)</Label>
              <Textarea id="tract-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Rues non faites, boîtes inaccessibles…" data-testid="tract-comment" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setStopOpen(false)} disabled={stopping}>Continuer</Button>
            <Button onClick={stop} disabled={stopping} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="tract-stop-confirm">
              {stopping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Clôturer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl bg-card border border-border p-3 text-center">
      <Icon className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
      <p className="font-semibold tabular-nums text-sm" data-testid={testId}>{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function TractSummary({ session, onClose }) {
  return (
    <div className="space-y-4" data-testid="tract-summary">
      <Card className="p-5 border-border shadow-sm text-center">
        <CheckCircle2 className="h-10 w-10 mx-auto text-[hsl(var(--brand-sage))] mb-2" />
        <h2 className="font-['Space_Grotesk'] text-xl font-semibold">Distribution terminée</h2>
        <p className="text-sm text-muted-foreground">{session.label} · {fmtShortDate(session.started_at)}</p>
      </Card>
      <RouteMap routes={[{ id: session.id, points: session.points, color: session.color, label: session.label }]} height={300} />
      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Route} label="Distance" value={formatDistance(session.distance_m)} testId="summary-distance" />
        <Stat icon={Timer} label="Durée" value={formatDuration(session.duration_s)} testId="summary-duration" />
        <Stat icon={Footprints} label="Tracts" value={session.flyers ?? '—'} />
      </div>
      <p className="text-xs text-muted-foreground text-center">{fmtTime(session.started_at)} → {fmtTime(session.ended_at)} · {session.point_count} points GPS · enregistré dans l'agenda Dolibarr</p>
      <Button onClick={onClose} size="lg" className="w-full h-12" variant="outline" data-testid="summary-close">Retour</Button>
    </div>
  );
}

function TractDetail({ sessionId, onBack, onUnauthorized }) {
  const [session, setSession] = useState(null);
  useEffect(() => {
    easerGet(`/api/easer/tracts/${sessionId}`).then((r) => setSession(r.data.session)).catch((err) => {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
      onBack();
    });
  }, [sessionId, onBack, onUnauthorized]);
  if (!session) return <Skeleton className="h-80 rounded-xl" />;
  return (
    <div className="space-y-4" data-testid="tract-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="tract-detail-back"><ArrowLeft className="h-4 w-4 mr-1" /> Mes distributions</Button>
      <div>
        <h2 className="font-['Space_Grotesk'] font-semibold">{session.label}</h2>
        <p className="text-sm text-muted-foreground">{fmtShortDate(session.started_at)} · {fmtTime(session.started_at)} → {fmtTime(session.ended_at)}</p>
      </div>
      <RouteMap routes={[{ id: session.id, points: session.points, color: session.color, label: session.label }]} height={320} />
      <div className="grid grid-cols-3 gap-2">
        <Stat icon={Route} label="Distance" value={formatDistance(session.distance_m)} />
        <Stat icon={Timer} label="Durée" value={formatDuration(session.duration_s)} />
        <Stat icon={Footprints} label="Tracts" value={session.flyers ?? '—'} />
      </div>
      {session.comment && <p className="text-sm text-muted-foreground italic">« {session.comment} »</p>}
    </div>
  );
}
